import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import { ErrorFallback } from "@/components/common/ErrorFallback";
import { usePermissions } from "@/hooks/usePermissions";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Edit,
  Ban,
  CheckCircle,
  Users,
  Server,
  Wallet,
  Settings,
  ArrowRightLeft,
  AlertTriangle,
} from "lucide-react";
import {
  useTenantDetail,
  useTenantMembers,
  useTenantResources,
  useTenantBilling,
} from "@/hooks/useTenantQueries";
import { useTenantManagementStore } from "@/stores/tenantManagementStore";
import { TenantQuotaEditor } from "@/components/tenants/TenantQuotaEditor";
import { TenantSuspensionModal } from "@/components/tenants/TenantSuspensionModal";
import { formatDate, formatDateTime } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import { EntityOrdersTab } from "@/components/orders/EntityOrdersTab";

// ============================================================================
// Component
// ============================================================================

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tenantId = id!;

  const {
    isQuotaEditorOpen,
    setQuotaEditorOpen,
    isSuspensionModalOpen,
    setSuspensionModalOpen,
  } = useTenantManagementStore();

  const { hasRole } = usePermissions();
  const canManageTenant = hasRole("SuperAdmin");
  const {
    data: tenant,
    isLoading,
    isFetching: isFetchingTenant,
    error: tenantError,
    refetch: refetchTenant,
  } = useTenantDetail(tenantId);
  const {
    data: members,
    isLoading: isLoadingMembers,
    isFetching: isFetchingMembers,
    error: membersError,
    refetch: refetchMembers,
  } = useTenantMembers(tenantId);
  const {
    data: resources,
    isLoading: isLoadingResources,
    isFetching: isFetchingResources,
    error: resourcesError,
    refetch: refetchResources,
  } = useTenantResources(tenantId);
  const {
    data: billing,
    isLoading: isLoadingBilling,
    isFetching: isFetchingBilling,
    error: billingError,
    refetch: refetchBilling,
  } = useTenantBilling(tenantId);

  // Whether a machine actually exists to transfer is unknown only while there is no resources
  // data at all (the initial load, or a failed initial load with nothing cached yet) — not on
  // every failed refetch. Suspend/activate/quota/edit all invalidate `tenantKeys.detail(id)`,
  // which is the prefix this query's key shares, so a routine write refetches it too; once the
  // list has been fetched successfully once, a later background failure keeps the cached count
  // rather than turning a known VM count back into "unknown" and disabling the button under it.
  const resourcesUnknown = !resources;
  const vmCount = resources?.vmCount ?? 0;

  // TenantDetailDto carries an accountBalance field, but GetTenantDetailsQueryHandler leaves
  // it hardcoded to null, so the header card used to render a flat 0 ₫ while the Billing tab
  // below showed the real figure. Both now read this single value, which comes from the
  // billing summary the page already fetches — the backend has converted it from the minor
  // unit using the tenant's own currency, so no conversion happens here.
  const currentBalance = billing?.currentBalance ?? billing?.availableBalance;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Full-screen only when there is nothing to show — a "Tenant not found" dead end used to cover
  // every failed read here, including a token refresh losing a race on an otherwise-good tenant.
  // Retry comes before the way out, the same as the resource detail page.
  if (!tenant) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <ErrorFallback
            error={tenantError as Error}
            onRetry={() => refetchTenant()}
          />
        </div>
        <div className="text-center">
          <Button asChild variant="outline">
            <Link to="/admin/tenants">Back to Tenants</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isTenantActive =
    tenant.status === "Active" || (tenant.status as unknown) === 1;
  const statusDisplay =
    (tenant.status as unknown) === 0 || tenant.status === "Pending"
      ? "Pending"
      : (tenant.status as unknown) === 1 || tenant.status === "Active"
        ? "Active"
        : (tenant.status as unknown) === 2 || tenant.status === "Suspended"
          ? "Suspended"
          : (tenant.status as unknown) === 3 || tenant.status === "Inactive"
            ? "Inactive"
            : String(tenant.status);
  const statusVariant =
    statusDisplay === "Active"
      ? "default"
      : statusDisplay === "Pending"
        ? "secondary"
        : "destructive";

  return (
    <div className="space-y-6">
      <PageHeader
        title={tenant.name}
        subtitle={`Created ${formatDate(tenant.createdAt)}`}
        titleAdornment={<Badge variant={statusVariant}>{statusDisplay}</Badge>}
        // Quotas, details, suspension and the ownership transfer all write to the tenant, and
        // every one of those endpoints is SuperAdmin-only.
        //
        // "Transfer VM" leads first: moving a machine between two customers is a job an operator
        // arrives at the customer page to do, and burying it one row deep inside the Resources tab
        // made it findable only by someone who already knew it was there. PageHeader keeps three
        // buttons and folds the rest away, so Manage Quotas — the least urgent of the four — is
        // the one that moves into the overflow menu.
        actions={
          !canManageTenant
            ? []
            : [
                {
                  label: "Transfer VM",
                  onClick: () =>
                    navigate(`/admin/tenants/${tenantId}/transfer-ownership`),
                  variant: "outline",
                  // No machines, nothing to hand over — said on the button rather than by an empty
                  // picker the operator has to open to discover it is empty. Also disabled while the
                  // resources query is still loading or has failed: `resourcesUnknown` keeps a blip on
                  // this one query from reading as a confirmed "this tenant has no machines".
                  disabled: resourcesUnknown || vmCount === 0,
                  icon: <ArrowRightLeft className="mr-2 h-4 w-4" />,
                },
                {
                  label: "Edit Details",
                  onClick: () => navigate(`/admin/tenants/${tenantId}/edit`),
                  variant: "outline",
                  icon: <Edit className="mr-2 h-4 w-4" />,
                },
                {
                  label: isTenantActive ? "Suspend" : "Activate",
                  onClick: () => setSuspensionModalOpen(true),
                  variant: isTenantActive ? "destructive" : "default",
                  icon: isTenantActive ? (
                    <Ban className="mr-2 h-4 w-4" />
                  ) : (
                    <CheckCircle className="mr-2 h-4 w-4" />
                  ),
                },
              ]
        }
      />

      {/* The tenant is in hand; a failed background refetch — most often the invalidation that
          follows a suspend/activate/quota/edit write — is reported here instead of dropping the
          status badge and every figure on this page back to stale silently. */}
      {tenantError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Could not refresh tenant details</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              The status badge and figures above may be showing a stale copy of
              this tenant.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetchTenant()}
              disabled={isFetchingTenant}
            >
              {isFetchingTenant && <Spinner className="mr-2" />}
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tenant.memberCount}</div>
            <p className="text-xs text-muted-foreground">Active users</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Resources</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tenant.resourceCount}</div>
            <p className="text-xs text-muted-foreground">Total VMs</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Balance</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                (currentBalance ?? 0) < 0 ? "text-destructive-ink" : ""
              }`}
            >
              {billing
                ? formatCurrency(currentBalance ?? 0, billing.currency || "VND")
                : "-"}
            </div>
            <p className="text-xs text-muted-foreground">Current balance</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="members">
            {/* Shown whenever there IS data, whatever the current fetch state — a "(0)" next to a
                tab whose request has never answered reads as a fact ("no members") rather than
                "unknown", but once the list has loaded once, a later background refetch failing
                (same reasoning as `resourcesUnknown` above) must not hide the count that is still
                sitting in the cache. */}
            Members{members !== undefined ? ` (${members.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="resources">
            Resources
            {resources !== undefined
              ? ` (${resources.totalResources ?? 0})`
              : ""}
          </TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Owner Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <div className="text-sm font-medium">Owner Email</div>
                <div className="text-sm text-muted-foreground">
                  {tenant.ownerEmail}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium">Owner Name</div>
                <div className="text-sm text-muted-foreground">
                  {tenant.ownerFullName}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle>Resource Quotas</CardTitle>
              {canManageTenant && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setQuotaEditorOpen(true)}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Manage Quotas
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Max Virtual Machines</span>
                <span className="font-medium">
                  {tenant.quotas.maxVms ??
                    tenant.quotas.maxVirtualMachines ??
                    0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Max Storage</span>
                <span className="font-medium">
                  {tenant.quotas.maxStorageGb ?? tenant.quotas.maxStorage ?? 0}{" "}
                  GB
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Max CPU Cores</span>
                <span className="font-medium">
                  {tenant.quotas.maxCpuCores ?? 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Max RAM</span>
                <span className="font-medium">
                  {tenant.quotas.maxRamGb ?? 0} GB
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members">
          <Card>
            <CardHeader>
              <CardTitle>Tenant Members</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingMembers ? (
                // Rows in the shape of the table rather than a bare "No members found" — that
                // line is a fact about the tenant, not something to claim before the request
                // asking for it has even answered.
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : membersError && !members ? (
                // Nothing cached to fall back on — this is the initial load failing, not a
                // background refetch, so the full-block Alert replacing the table is correct.
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Failed to load members</AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>
                      {(membersError as Error).message ||
                        "An unexpected error occurred."}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => refetchMembers()}
                    >
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  {/* A background refetch failed (most often the invalidation that follows a
                      suspend/activate/quota/edit write) but the previous list is still cached —
                      report it in a banner above the kept rows rather than throwing the table
                      away for a request that has nothing to do with the members themselves. */}
                  {membersError && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Could not refresh members</AlertTitle>
                      <AlertDescription className="space-y-2">
                        <p>The list below may be showing a stale copy.</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => refetchMembers()}
                          disabled={isFetchingMembers}
                        >
                          {isFetchingMembers && <Spinner className="mr-2" />}
                          Retry
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}
                  {members && members.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Joined</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {members.map((member) => (
                          <TableRow key={member.id}>
                            <TableCell className="font-medium">
                              {member.fullName}
                            </TableCell>
                            <TableCell>{member.email}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{member.role}</Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(member.joinedAt)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No members found
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources">
          <Card>
            <CardHeader>
              <CardTitle>Tenant Resources</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingResources ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : resourcesError && !resources ? (
                // Nothing cached to fall back on — the initial load failing, not a background
                // refetch.
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Failed to load resources</AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>
                      {(resourcesError as Error).message ||
                        "An unexpected error occurred."}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => refetchResources()}
                    >
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  {/* A background refetch failed but the previous list is still cached — report it
                      above the kept rows rather than replacing the table (and, indirectly, the
                      "Transfer VM" button's own data). */}
                  {resourcesError && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Could not refresh resources</AlertTitle>
                      <AlertDescription className="space-y-2">
                        <p>The list below may be showing a stale copy.</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => refetchResources()}
                          disabled={isFetchingResources}
                        >
                          {isFetchingResources && <Spinner className="mr-2" />}
                          Retry
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}
                  {resources &&
                  resources.resources &&
                  resources.resources.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {resources.resources.map((resource) => (
                          <TableRow key={resource.id}>
                            <TableCell className="font-medium">
                              {resource.name}
                            </TableCell>
                            <TableCell>{resource.type}</TableCell>
                            <TableCell>
                              <Badge>{resource.status}</Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(resource.createdAt)}
                            </TableCell>
                            <TableCell className="text-right">
                              {resource.type === "VirtualMachine" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    navigate(
                                      `/admin/tenants/${tenantId}/resources/${resource.id}/transfer-ownership`,
                                    )
                                  }
                                >
                                  <ArrowRightLeft className="h-3.5 w-3.5" />
                                  Transfer
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No resources found
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="orders">
          <EntityOrdersTab tenantId={tenantId} />
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing">
          <Card>
            <CardHeader>
              <CardTitle>Recent Payments</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingBilling ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : billingError && !billing ? (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Failed to load billing summary</AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>
                      {(billingError as Error).message ||
                        "An unexpected error occurred."}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => refetchBilling()}
                    >
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  {billingError && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Could not refresh billing summary</AlertTitle>
                      <AlertDescription className="space-y-2">
                        <p>The figures below may be showing a stale copy.</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => refetchBilling()}
                          disabled={isFetchingBilling}
                        >
                          {isFetchingBilling && <Spinner className="mr-2" />}
                          Retry
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}
                  {billing &&
                  billing.recentPayments &&
                  billing.recentPayments.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Payment ID</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {billing.recentPayments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell className="font-mono text-xs">
                              {payment.id.slice(0, 8)}...
                            </TableCell>
                            <TableCell>{payment.paymentMethod}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{payment.status}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(
                                payment.amount,
                                billing?.currency || "VND",
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDateTime(payment.createdAt)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No payments found
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <TenantQuotaEditor
        tenantId={tenantId}
        currentQuotas={tenant.quotas}
        open={isQuotaEditorOpen}
        onOpenChange={setQuotaEditorOpen}
      />

      <TenantSuspensionModal
        tenantId={tenantId}
        tenantName={tenant.name}
        currentStatus={tenant.status}
        open={isSuspensionModalOpen}
        onOpenChange={setSuspensionModalOpen}
      />
    </div>
  );
}
